import { Button, Checkbox, Collapse, FormControlLabel, Grid, TextField } from '@material-ui/core';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import cx from 'classnames';
import MuiPhoneNumber from 'material-ui-phone-number';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ReusableSelect } from '../reusable/select';
import { ButtonWithLoader } from 'components/reusable/button-loader';
import { setAccount, setMessages } from 'store/actions/actions';
import { useGlobalState } from 'store/reducers/reducer';
import { updateAccountInfo, updateAccountTerms } from 'store/sagas/sagas';
import { SUBSCRIPTION_TIERS } from 'utils/billing';
import { removeSpace } from 'utils/ui.utils';
import './update-account-info.css';
const CURRENT_TERMS_VERSION = '1.3';
export const UpdateAccountInfo = () => {
    const { state, dispatch, asyncDispatch } = useGlobalState();
    const navigate = useNavigate();
    const stripe = useStripe();
    const elements = useElements();
    const account = {
        ...state.account
    };
    const user = {
        ...state.user
    };
    const [country, setCountry] = useState(null);
    const [street, setStreet] = useState(null);
    const [postcode, setPostcode] = useState(null);
    const [province, setProvince] = useState(null);
    const [city, setCity] = useState(null);
    const [companyName, setCompanyName] = useState('');
    const [companySize, setCompanySize] = useState('');
    const handleCompanySize = useCallback((e) => {
        setCompanySize(e.target.value);
    }, []);
    const [companyIndustry, setCompanyIndustry] = useState('');
    const handleCompanyIndustry = useCallback((e) => {
        setCompanyIndustry(e.target.value);
    }, []);
    const changingDisabled = !user.emailVerified || !user.isAccountAdmin;
    const [invalidData, setInvalidData] = useState(false);
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [phone, setPhone] = useState('');
    const [collapsibleOpen, setCollapsibleOpen] = useState(false);
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [termsVersion, setTermsVersion] = useState('');
    useEffect(() => {
        const getInitialCount = () => {
            const count = account.setupAccountStep ?? '';
            switch (count) {
                case 'COMPANY':
                    return 1;
                case 'TERMS':
                    return 2;
                case 'BILLING':
                    return 3;
                default:
                    return 0;
            }
        };
        setCount(getInitialCount());
    }, [account.setupAccountStep]);
    const [count, setCount] = useState(0);
    const handleChangeFirstName = useCallback((e) => {
        setInvalidData(false);
        setFirstName(e.target.value);
    }, []);
    const handleChangeLastName = useCallback((e) => {
        setInvalidData(false);
        setLastName(e.target.value);
    }, []);
    const handleChangePhone = useCallback((e) => {
        /*
         * Cursed knowledge: The `material-ui-phone-number` package ships bad types, this will never be an event,
         * this is just to make the compiler happy :)
         */
        if (typeof e !== 'string') {
            return;
        }
        setInvalidData(false);
        setPhone(e);
    }, []);
    const handleChangeCompanyName = useCallback((e) => {
        setInvalidData(false);
        setCompanyName(e.target.value);
    }, []);
    const handleChangeCompanyWebsite = useCallback((e) => {
        setInvalidData(false);
        setCompanyWebsite(e.target.value);
    }, []);
    const handleChangeCity = useCallback((e) => {
        setInvalidData(false);
        setCity(e.target.value);
    }, []);
    const handleChangeStreet = useCallback((e) => {
        setInvalidData(false);
        setStreet(e.target.value);
    }, []);
    const handleChangeProvince = useCallback((e) => {
        setInvalidData(false);
        setProvince(e.target.value);
    }, []);
    const handleChangeCountry = useCallback((e) => {
        setInvalidData(false);
        setCountry(e.target.value);
    }, []);
    const handlePostalCode = useCallback((e) => {
        setInvalidData(false);
        setPostcode(removeSpace(e.target.value));
    }, []);
    const [companyWebsite, setCompanyWebsite] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const companyInfoPost = () => {
        if (!street || !city || !province || !country || !postcode) {
            setInvalidData(true);
            return;
        }
        if (companyName === '' ||
            companyIndustry === '' ||
            !companySize ||
            companyWebsite === '') {
            setInvalidData(true);
            return;
        }
        setIsLoading(true);
        void asyncDispatch(updateAccountInfo({
            name: companyName,
            url: companyWebsite,
            industry: companyIndustry,
            companySize,
            setupAccountStep: 'TERMS',
            country,
            province,
            city,
            street,
            postalCode: postcode
        }))
            .then((data) => {
            dispatch(setMessages({
                value: 'Information updated successfully',
                severity: 'success'
            }));
            dispatch(setAccount(data.updatedAccount));
            setInvalidData(false);
            setCount(count + 1);
        })
            .finally(() => {
            setIsLoading(false);
        });
    };
    const termsPost = () => {
        if (!termsAccepted || !termsVersion) {
            setInvalidData(true);
            dispatch(setMessages({
                value: 'Please accept the terms and conditions by selecting the checkbox below the terms and conditions',
                severity: 'error'
            }));
            return;
        }
        setIsLoading(true);
        void asyncDispatch(updateAccountTerms({
            setupAccountStep: 'BILLING',
            termsVersion
        }))
            .then((data) => {
            dispatch(setMessages({
                value: 'Terms acceptance saved!',
                severity: 'success'
            }));
            dispatch(setAccount(data.updatedAccount));
            setInvalidData(false);
            setCount(count + 1);
            if (data.updatedAccount.setupStatus === 'COMPLETED') {
                navigate('/account/get-started');
            }
        })
            .finally(() => {
            setIsLoading(false);
        });
    };
    const contactInfoPost = () => {
        if (firstName === '' || lastName === '' || phone === '') {
            setInvalidData(true);
            return;
        }
        setIsLoading(true);
        asyncDispatch(updateAccountInfo({
            firstName,
            lastName,
            phoneNumber: phone,
            setupAccountStep: 'COMPANY'
        }))
            .then((data) => {
            dispatch(setAccount(data.updatedAccount));
            setIsLoading(false);
            dispatch(setMessages({
                value: 'Information updated successfully',
                severity: 'success'
            }));
            setCount(count + 1);
        })
            .catch((error) => {
            console.error(error);
            setIsLoading(false);
        });
    };
    const saveAndContinue = (step) => {
        switch (step) {
            case 0:
                contactInfoPost();
                break;
            case 1:
                companyInfoPost();
                break;
            case 2:
                termsPost();
                break;
            case 3:
                break;
        }
    };
    const rejectTerms = () => {
        dispatch(setMessages({
            value: 
            // eslint-disable-next-line max-len
            'Thank you for your interest in Vouched.  Please accept our Terms of Service to use the Vouched Services.  Otherwise, please contact our sales team to get set-up with Vouched by emailing hello@vouched.id',
            severity: 'info'
        }));
    };
    const submit = (event) => {
        setIsLoading(true);
        event.preventDefault();
        if (!elements || !stripe) {
            setIsLoading(false);
            return;
        }
        const cardElement = elements.getElement(CardElement);
        if (!cardElement) {
            setIsLoading(false);
            return;
        }
        stripe
            .createPaymentMethod({
            type: 'card',
            card: cardElement
        })
            .then(async ({ error, paymentMethod }) => {
            if (error) {
                console.error(error);
                const message = error.type === 'validation_error'
                    ? (error.message ?? 'n/a')
                    : 'Error validating your card information.';
                dispatch(setMessages({
                    value: message,
                    severity: 'error'
                }));
                setIsLoading(false);
                return;
            }
            try {
                const data = await asyncDispatch(updateAccountInfo({
                    paymentMethodId: paymentMethod.id
                }));
                dispatch(setAccount(data.updatedAccount));
                navigate('/account/get-started');
                setIsLoading(false);
            }
            catch (error_) {
                console.error(error_);
                setIsLoading(false);
                setInvalidData(true);
            }
        })
            .catch(console.error);
    };
    const toggleCollapsible = () => {
        setCollapsibleOpen(!collapsibleOpen);
    };
    /*
     * TODO use SUBSCRIPTION_TIER_NO_PAYMENT_REQUIRED from onboard-service account.model.ts once we have common libraries
     * Until then make sure this constant has the same value
     */
    const billingInfoRequired = account.subscriptionTier !== SUBSCRIPTION_TIERS.TRIAL;
    return (React.createElement("div", { className: "update-account-main" },
        React.createElement("h1", null, "Set up your account"),
        React.createElement("div", { className: "update-account-tabs" },
            React.createElement("div", { style: {
                    display: 'flex',
                    flex: 1,
                    justifyContent: 'center',
                    alignItems: 'center',
                    borderBottom: 'solid',
                    borderWidth: count === 0 ? '4px' : '2px',
                    borderColor: count === 0 ? '#635BFF' : 'inherit',
                    padding: '16px'
                } },
                React.createElement("span", { style: {
                        fontSize: '16px',
                        color: count === 0 ? '#635BFF' : 'inherit',
                        lineHeight: '18px',
                        fontWeight: 600
                    } }, "Step 1: Contact Info")),
            React.createElement("div", { style: {
                    display: 'flex',
                    flex: 1,
                    justifyContent: 'center',
                    alignItems: 'center',
                    borderBottom: 'solid',
                    borderWidth: count === 1 ? '4px' : '2px',
                    borderColor: count === 1 ? '#635BFF' : 'inherit',
                    padding: '16px'
                } },
                React.createElement("span", { style: {
                        fontSize: '16px',
                        color: count === 1 ? '#635BFF' : 'inherit',
                        lineHeight: '18px',
                        fontWeight: 600
                    } }, "Step 2: Company Info")),
            React.createElement("div", { style: {
                    display: 'flex',
                    flex: 1,
                    justifyContent: 'center',
                    alignItems: 'center',
                    borderBottom: 'solid',
                    borderWidth: count === 2 ? '4px' : '2px',
                    borderColor: count === 2 ? '#635BFF' : 'inherit',
                    padding: '16px'
                } },
                React.createElement("span", { style: {
                        fontSize: '16px',
                        color: count === 2 ? '#635BFF' : 'inherit',
                        lineHeight: '18px',
                        fontWeight: 600
                    } }, "Step 3: Accept Terms and Conditions")),
            React.createElement("div", { style: {
                    display: 'flex',
                    flex: 1,
                    justifyContent: 'center',
                    alignItems: 'center',
                    borderBottom: 'solid',
                    borderWidth: count === 2 ? '4px' : '2px',
                    borderColor: count === 2 ? '#635BFF' : 'inherit',
                    padding: '16px'
                } }, billingInfoRequired && (React.createElement("span", { style: {
                    fontSize: '16px',
                    color: count === 3 ? '#635BFF' : 'inherit',
                    lineHeight: '18px',
                    fontWeight: 600
                } }, "Step 4: Billing Info")))),
        count === 0 && (React.createElement("div", { style: {
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                width: '100%'
            } },
            React.createElement("h2", null, "Contact Info"),
            React.createElement("span", { style: {
                    fontSize: '16px',
                    color: '#444444',
                    lineHeight: '24px',
                    fontWeight: 500,
                    marginBottom: '3vh'
                } }, "To complete the sign up process we need to verify your contact information."),
            React.createElement("span", { style: {
                    color: '#999999',
                    fontSize: '16px',
                    fontWeight: 800,
                    lineHeight: '24px',
                    marginBottom: '2vh'
                } }, "Contact Information"),
            React.createElement(TextField, { className: "g-input-height u-margin-bottom-medium", error: firstName === '' && invalidData, helperText: firstName ? '' : 'Invalid First name.', onChange: handleChangeFirstName, placeholder: "First name", required: true }),
            React.createElement(TextField, { className: "g-input-height u-margin-bottom-medium", error: lastName === '' && invalidData, helperText: lastName ? '' : 'Invalid Last name.', onChange: handleChangeLastName, placeholder: "Last name", required: true }),
            React.createElement(MuiPhoneNumber, { defaultCountry: "us", onChange: handleChangePhone }),
            React.createElement("div", { style: {
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    marginTop: '1vh'
                } },
                React.createElement(Button, { onClick: () => {
                        saveAndContinue(0);
                    } }, "Save and continue")))),
        count === 1 && (React.createElement("div", { style: {
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                width: '100%'
            } },
            React.createElement("h2", null, "Company Info"),
            React.createElement("span", { style: {
                    fontSize: '16px',
                    color: '#444444',
                    lineHeight: '24px',
                    fontWeight: 500,
                    marginBottom: '3vh'
                } }, "Help us understand what you do."),
            React.createElement("span", { style: {
                    color: '#999999',
                    fontSize: '16px',
                    fontWeight: 800,
                    lineHeight: '24px',
                    marginBottom: '2vh'
                } }, "Company Information"),
            React.createElement(TextField, { className: "g-input-height u-margin-bottom-medium", error: companyName === '' && invalidData, helperText: companyName ? '' : 'Invalid Company name.', onChange: handleChangeCompanyName, placeholder: "Company name", required: true }),
            React.createElement(TextField, { className: "g-input-height u-margin-bottom-medium", error: companyWebsite === '' && invalidData, helperText: companyWebsite ? '' : 'Invalid Company Website.', onChange: handleChangeCompanyWebsite, placeholder: "Company website", required: true }),
            React.createElement("div", { style: {
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    marginBottom: '18px'
                } },
                companyIndustry === '' && (React.createElement("span", { style: {
                        position: 'absolute',
                        zIndex: 1,
                        fontSize: '0.8125rem',
                        color: '#7f90a0'
                    } }, "Industry")),
                React.createElement(ReusableSelect, { className: "u-margin-bottom-small", items: [
                        { name: 'Accounting', id: 'Accounting' },
                        {
                            name: 'Administration & Office Support',
                            id: 'Administration & Office Support'
                        },
                        {
                            name: 'Advertising, Arts & Media',
                            id: 'Advertising, Arts & Media'
                        },
                        {
                            name: 'Banking & Financial Services',
                            id: 'Banking & Financial Services'
                        },
                        {
                            name: 'Call Centre & Customer Service',
                            id: 'Call Centre & Customer Service'
                        },
                        {
                            name: 'Community Services & Development',
                            id: 'Community Services & Development'
                        },
                        { name: 'Construction', id: 'Construction' },
                        {
                            name: 'Consulting & Strategy',
                            id: 'Consulting & Strategy'
                        },
                        {
                            name: 'Design & Architecture',
                            id: 'Design & Architecture'
                        },
                        { name: 'Education & Training', id: 'Education & Training' },
                        { name: 'Engineering', id: 'Engineering' },
                        {
                            name: 'Farming, Animals & Conservation',
                            id: 'Farming, Animals & Conservation'
                        },
                        { name: 'Government & Defence', id: 'Government & Defence' },
                        { name: 'Healthcare & Medical', id: 'Healthcare & Medical' },
                        {
                            name: 'Hospitality & Tourism',
                            id: 'Hospitality & Tourism'
                        },
                        {
                            name: 'Human Resources & Recruitment',
                            id: 'Human Resources & Recruitment'
                        },
                        {
                            name: 'Information & Communication Technology',
                            id: 'Information & Communication Technology'
                        },
                        {
                            name: 'Insurance & Superannuation',
                            id: 'Insurance & Superannuation'
                        },
                        { name: 'Legal', id: 'Legal' },
                        {
                            name: 'Manufacturing, Transport & Logistics',
                            id: 'Manufacturing, Transport & Logistics'
                        },
                        {
                            name: 'Marketing & Communications',
                            id: 'Marketing & Communications'
                        },
                        {
                            name: 'Mining, Resources & Energy',
                            id: 'Mining, Resources & Energy'
                        },
                        {
                            name: 'Real Estate & Property',
                            id: 'Real Estate & Property'
                        },
                        {
                            name: 'Retail & Consumer Products',
                            id: 'Retail & Consumer Products'
                        },
                        { name: 'Sales', id: 'Sales' },
                        { name: 'Science & Technology', id: 'Science & Technology' },
                        { name: 'Sport & Recreation', id: 'Sport & Recreation' },
                        { name: 'Services', id: 'Services' }
                    ], onChange: handleCompanyIndustry, placeholder: "Accounting", style: {
                        width: '100%',
                        borderBottom: companyIndustry === '' && invalidData
                            ? '2px solid red'
                            : '#aaaaaa '
                    }, value: companyIndustry })),
            React.createElement("div", { style: {
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    marginBottom: '18px'
                } },
                !companySize && (React.createElement("span", { style: {
                        position: 'absolute',
                        zIndex: 1,
                        fontSize: '0.8125rem',
                        color: '#7f90a0'
                    } }, "Number of employees")),
                React.createElement(ReusableSelect, { className: "u-margin-bottom-small", defaultValue: account.companySize ?? '0-10', disabled: changingDisabled, items: [
                        { id: '0-10', name: '0 - 10' },
                        { id: '10-100', name: '10 - 100' },
                        { id: '+100', name: 'more than 100' }
                    ], onChange: handleCompanySize, placeholder: "Industry", style: {
                        borderBottom: !companySize && invalidData ? '2px solid red' : '#aaaaaa '
                    }, value: companySize })),
            React.createElement(Grid, { item: true, xs: 12 },
                React.createElement(TextField, { className: "g-input-height u-margin-bottom-medium location-field", error: !street && invalidData, helperText: street ? '' : 'Invalid Street', onChange: handleChangeStreet, placeholder: "Street", required: true, value: street }),
                React.createElement(TextField, { className: "g-input-height u-margin-bottom-medium location-field", error: !city && invalidData, helperText: city ? '' : 'Invalid City', onChange: handleChangeCity, placeholder: "City", required: true, value: city }),
                React.createElement(TextField, { className: "g-input-height u-margin-bottom-medium location-field", error: !province && invalidData, helperText: province ? '' : 'Invalid State/Province', onChange: handleChangeProvince, placeholder: "State/Province", required: true, value: province }),
                React.createElement(TextField, { className: "g-input-height u-margin-bottom-medium location-field", error: !country && invalidData, helperText: country ? '' : 'Invalid Country', onChange: handleChangeCountry, placeholder: "Country", required: true, value: country }),
                React.createElement(TextField, { className: "g-input-height u-margin-bottom-medium location-field", error: !postcode && invalidData, helperText: postcode ? '' : 'Invalid Postal Code', onChange: handlePostalCode, placeholder: "Postal Code", required: true, value: postcode })),
            React.createElement("div", { style: {
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center'
                } },
                React.createElement(Button, { onClick: () => {
                        saveAndContinue(1);
                    } }, "Save and continue")))),
        count === 2 && (React.createElement("div", { style: {
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                width: '100%'
            } },
            React.createElement("div", { style: {
                    marginBottom: '50px'
                } },
                "Here are the",
                ' ',
                React.createElement("a", { href: "https://www.vouched.id/company/legal/terms-of-service", rel: "noreferrer", style: {
                        fontWeight: '600',
                        color: '#635BFF'
                    }, target: "_blank" }, "terms and conditions"),
                ' ',
                "for using the Vouched verification service. Please accept them before using Vouched"),
            React.createElement("div", null,
                React.createElement(FormControlLabel, { control: React.createElement(Checkbox, { "aria-label": "Sample", "aria-labelledby": "Hello", checked: termsAccepted, onChange: () => {
                            setTermsAccepted(!termsAccepted);
                            setTermsVersion(CURRENT_TERMS_VERSION);
                        }, size: "medium" }), label: React.createElement("span", null,
                        "By checking this box, I acknowledge that have read and",
                        React.createElement("br", null),
                        "consent to the terms and conditions put forward by Vouched"), style: {
                        marginBottom: '50px'
                    } })),
            React.createElement("div", { style: {
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                } },
                React.createElement(Button, { onClick: () => {
                        saveAndContinue(2);
                    }, style: { margin: '8px' } }, "Accept Terms and Conditions"),
                React.createElement(Button, { onClick: rejectTerms, style: { margin: '8px' } }, "Reject Terms and Conditions")))),
        count === 3 && (React.createElement("div", { style: {
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                width: '100%'
            } },
            React.createElement("h2", null, "Billing Info"),
            React.createElement("span", { style: {
                    fontSize: '16px',
                    color: '#444444',
                    lineHeight: '24px',
                    fontWeight: 500,
                    marginBottom: '3vh'
                } }, "When you click \"Finish Account Setup\", a 14 day free trial will begin on your account. You will not be charged until the end of the free trial"),
            (!account.subscriptionTier ||
                account.subscriptionTier === SUBSCRIPTION_TIERS.SELF_SERVICE) && (React.createElement("div", { style: {
                    background: 'white',
                    borderRadius: '5px',
                    padding: '15px',
                    display: 'flex',
                    flexDirection: 'column',
                    marginBottom: '2vh'
                } },
                React.createElement("div", null,
                    React.createElement("span", { style: {
                            fontWeight: 'bold',
                            fontSize: '24px',
                            color: '#444444',
                            lineHeight: '28px'
                        } }, "Base subscription")),
                React.createElement("div", { style: {
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between'
                    } },
                    React.createElement("span", { style: {
                            fontWeight: 'bold',
                            fontSize: '24px',
                            color: collapsibleOpen ? '#999999' : 'inherit',
                            lineHeight: '28px'
                        } }, "$300/month"),
                    React.createElement("div", { onClick: toggleCollapsible },
                        React.createElement("span", { style: {
                                fontWeight: 'bold',
                                fontSize: '16px',
                                color: '#999999',
                                lineHeight: '24px'
                            } }, "What's included?"),
                        React.createElement("i", { className: collapsibleOpen
                                ? cx('fa fa-angle-up')
                                : cx('fas fa-angle-down'), style: {
                                color: '#999999',
                                fontSize: '15px',
                                padding: '0px 5px'
                            } }))),
                React.createElement(Collapse, { in: collapsibleOpen },
                    React.createElement("div", { style: { marginTop: '1vh' } },
                        React.createElement("div", { style: { marginBottom: '2vh' } },
                            React.createElement("span", { style: {
                                    fontWeight: 500,
                                    fontSize: '16px',
                                    lineHeight: '24px',
                                    color: '#444444'
                                } }, "Verify and onboard new customers, patients, partners and gig contractors in seconds. With Vouched ID verification, you can expand your reach while keeping the bad guys out.")),
                        React.createElement("div", { style: {
                                padding: '10px 0px',
                                borderTop: 'solid 1px #eeeeee'
                            } }),
                        React.createElement("div", null,
                            React.createElement("div", { style: { marginBottom: '2vh' } },
                                React.createElement("span", { style: {
                                        fontWeight: 800,
                                        fontSize: '16px',
                                        lineHeight: '24px',
                                        color: '#999999'
                                    } }, "Included features")),
                            React.createElement("div", { style: {
                                    display: 'flex',
                                    flexDirection: 'row',
                                    justifyContent: 'space-between'
                                } },
                                React.createElement("div", { style: { display: 'flex', flexDirection: 'column' } },
                                    React.createElement("div", { style: {
                                            fontSize: '14px',
                                            fontWeight: 500,
                                            lineHeight: '24px',
                                            color: '#444444'
                                        } }, "Expert ID Document Examination"),
                                    React.createElement("div", { style: {
                                            fontSize: '14px',
                                            fontWeight: 500,
                                            lineHeight: '24px',
                                            color: '#444444'
                                        } }, "Facial Recognition, Liveness Detection"),
                                    React.createElement("div", { style: {
                                            fontSize: '14px',
                                            fontWeight: 500,
                                            lineHeight: '24px',
                                            color: '#444444'
                                        } }, "Data Extraction"),
                                    React.createElement("div", { style: {
                                            fontSize: '14px',
                                            fontWeight: 500,
                                            lineHeight: '24px',
                                            color: '#444444'
                                        } }, "Dashboard Access")),
                                React.createElement("div", { style: { display: 'flex', flexDirection: 'column' } },
                                    React.createElement("div", { style: {
                                            fontSize: '14px',
                                            fontWeight: 500,
                                            lineHeight: '24px',
                                            color: '#444444'
                                        } }, "Anonymous Activity"),
                                    React.createElement("div", { style: {
                                            fontSize: '14px',
                                            fontWeight: 500,
                                            lineHeight: '24px',
                                            color: '#444444'
                                        } }, "Analysis of email, phone, address, name"),
                                    React.createElement("div", { style: {
                                            fontSize: '14px',
                                            fontWeight: 500,
                                            lineHeight: '24px',
                                            color: '#444444'
                                        } }, "Email age and fraud risk"),
                                    React.createElement("div", { style: {
                                            fontSize: '14px',
                                            fontWeight: 500,
                                            lineHeight: '24px',
                                            color: '#444444'
                                        } }, "Phone source")))))))),
            React.createElement("div", null,
                React.createElement("div", { className: "g-page-without-header-footer", style: {
                        display: 'flex'
                    } },
                    React.createElement("div", { className: "u-align-horizontal-center" },
                        React.createElement("form", { autoComplete: "off", className: "g-form", noValidate: true, onSubmit: submit },
                            React.createElement("form", null,
                                React.createElement(CardElement, { options: {
                                        style: {
                                            base: {
                                                fontSize: '16px',
                                                color: '#424770',
                                                '::placeholder': {
                                                    color: '#aab7c4'
                                                }
                                            },
                                            invalid: {
                                                color: '#9e2146'
                                            }
                                        }
                                    } })),
                            React.createElement("div", null,
                                React.createElement("div", { className: "subscription-confirm-container" },
                                    React.createElement("div", null, "By clicking \"Finish Account Setup\" you will start your subscription"))),
                            React.createElement("div", { style: {
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    alignItems: 'center'
                                } },
                                React.createElement(ButtonWithLoader, { disabled: isLoading, isLoading: isLoading, name: "Finish Account Setup" }))))))))));
};
